import type { GameSettings } from "#/game/store/types";

const GUEST_GAMEPLAY_SETTINGS_STORAGE_KEY = "guestGameplaySettings";

export type GuestGameplaySettingKey =
  | keyof Pick<GameSettings, "endTurnConfirmation" | "disableAiPlayback" | "captureResetWarning">
  | "fastAnimations";

type GuestGameplaySettingsRecord = Partial<Record<GuestGameplaySettingKey, boolean>>;

const GUEST_GAMEPLAY_SETTING_KEYS = [
  "endTurnConfirmation",
  "fastAnimations",
  "disableAiPlayback",
  "captureResetWarning",
] as const satisfies readonly GuestGameplaySettingKey[];

export type GuestGameplaySettingsSnapshot = Record<GuestGameplaySettingKey, boolean>;

function parseGuestGameplaySettings(raw: string | null): GuestGameplaySettingsRecord {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const record = parsed as Record<string, unknown>;
    const settings: GuestGameplaySettingsRecord = {};
    for (const key of GUEST_GAMEPLAY_SETTING_KEYS) {
      const value = record[key];
      if (typeof value === "boolean") {
        settings[key] = value;
      }
    }
    return settings;
  } catch {
    return {};
  }
}

export function readGuestGameplaySettings(): GuestGameplaySettingsRecord {
  if (typeof window === "undefined") return {};
  return parseGuestGameplaySettings(
    window.localStorage.getItem(GUEST_GAMEPLAY_SETTINGS_STORAGE_KEY),
  );
}

export function writeGuestGameplaySetting({
  setting,
  enabled,
}: {
  setting: GuestGameplaySettingKey;
  enabled: boolean;
}): void {
  if (typeof window === "undefined") return;
  const next = { ...readGuestGameplaySettings(), [setting]: enabled };
  window.localStorage.setItem(GUEST_GAMEPLAY_SETTINGS_STORAGE_KEY, JSON.stringify(next));
}

export function mergeGuestGameplaySettings(
  defaults: GuestGameplaySettingsSnapshot,
): GuestGameplaySettingsSnapshot {
  return { ...defaults, ...readGuestGameplaySettings() };
}

export function gameSettingsForSession({
  isAnonymous,
  endTurnConfirmation,
  fastAnimations,
  disableAiPlayback,
  captureResetWarning,
}: {
  isAnonymous: boolean;
} & GuestGameplaySettingsSnapshot): GameSettings {
  const gameplay = isAnonymous
    ? mergeGuestGameplaySettings({
        endTurnConfirmation,
        fastAnimations,
        disableAiPlayback,
        captureResetWarning,
      })
    : { endTurnConfirmation, fastAnimations, disableAiPlayback, captureResetWarning };

  return {
    fastPlayback: gameplay.fastAnimations,
    endTurnConfirmation: gameplay.endTurnConfirmation,
    disableAiPlayback: gameplay.disableAiPlayback,
    captureResetWarning: gameplay.captureResetWarning,
  };
}
